import { useUnleashUserContextReady } from '@/components/UnleashProvider'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useAssessmentsAccess } from '@/hooks/feature-access/useAssessmentsAccess'
import { useLearnerCoachingEnabled } from '@/hooks/useLearnerCoachingEnabled'
import { useMandatoryAssessmentRequired } from '@/hooks/useMandatoryAssessmentRequired'
import { useNeedsAnalysisStatus } from '@/hooks/useNeedsAnalysisStatus'
import { useOrgGroupAccess } from '@/hooks/useOrgGroupAccess'
import { useRole } from '@/hooks/useRole'
import { useUserpilotSignOutReset } from '@/hooks/useUserpilotSignOutReset'
import { formatRoleName } from '@/lib/utils'
import { ClerkUserRole } from '@/types/auth'
import { FeatureAccessPath } from '@/types/configuration'
import { toIso6391LanguageCode } from '@/utils/languageCodeUtils'
import { isEligibilityExpired, trialWindowDaysRemaining, trialWindowEndDate } from '@/utils/trialFormatters'
import { useClerk, useUser } from '@clerk/clerk-react'
import { useFlag, useFlagsStatus } from '@unleash/proxy-client-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Userpilot } from 'userpilot'

// The app-owned modal still pending, so Userpilot content does not stack on it.
type OnboardingStage = 'needs_analysis' | 'assessment' | 'ready'

const SIGNIN_TRACKED_SESSIONS_KEY = 'userpilot:tracked_signin_sessions'

// The same as the /users/me deduping interval.
const FOCUS_REFRESH_INTERVAL_MS = 60_000

// The Userpilot SDK drops a null property and sends nothing, so only an empty string clears an old value.
const CLEARED = ''

function hasTrackedSignIn(sessionId: string): boolean {
  try {
    const raw = localStorage.getItem(SIGNIN_TRACKED_SESSIONS_KEY)
    return raw ? raw.split(',').includes(sessionId) : false
  } catch {
    return false
  }
}

function markSignInTracked(sessionId: string): void {
  try {
    const raw = localStorage.getItem(SIGNIN_TRACKED_SESSIONS_KEY)
    const ids = raw ? raw.split(',') : []
    if (!ids.includes(sessionId)) {
      ids.push(sessionId)
      localStorage.setItem(SIGNIN_TRACKED_SESSIONS_KEY, ids.slice(-5).join(','))
    }
  } catch {
    // localStorage unavailable (private mode, etc.) — fail open and skip dedup
  }
}

/** Identifies the user in Userpilot, and tracks `user_signed_in` once per Clerk session. */
export function useUserpilotIdentify(): void {
  // First, so its unsuppress runs before the identify effect below on sign-in.
  useUserpilotSignOutReset()
  const { user } = useUser()
  const clerk = useClerk()
  const { user: userDetails, refetch: refetchCurrentUser } = useCurrentUser()
  const groupName = userDetails?.group_name
  const { userRole } = useRole()
  const { i18n } = useTranslation()
  // The language the app chrome is actually rendering in, so tours match it.
  const localeCode = toIso6391LanguageCode(i18n.resolvedLanguage ?? i18n.language)

  // Featured Lessons cohort (ENG-2823), from Unleash so it matches what the user sees.
  const featuredLessonsEnabled = useFlag('homepage-featured-lessons')
  const { flagsReady } = useFlagsStatus()
  // The learner-coaching flag targets user ids, so it is only final after the user context push.
  const learnerCoachingFlag = useLearnerCoachingEnabled()
  const unleashUserContextReady = useUnleashUserContextReady()
  const coachingFlagReady = flagsReady && unleashUserContextReady

  const {
    hasCompletedNeedsAnalysis,
    error: needsAnalysisError,
    isLoading: isNeedsAnalysisLoading,
  } = useNeedsAnalysisStatus()
  const {
    shouldShowMandatoryAssessment,
    assessmentDueAt,
    isLoading: isAssessmentLoading,
  } = useMandatoryAssessmentRequired()
  const onboardingReady = !isNeedsAnalysisLoading && !isAssessmentLoading
  // Mirrors the guards: only learners are prompted, and a failed lookup shows no modal.
  let onboardingStage: OnboardingStage = 'ready'
  if (userRole === ClerkUserRole.LEARNER && !needsAnalysisError && !hasCompletedNeedsAnalysis)
    onboardingStage = 'needs_analysis'
  else if (shouldShowMandatoryAssessment) onboardingStage = 'assessment'
  const assessmentDueAtIso = assessmentDueAt?.toISOString() ?? null

  // Real access, resolved as the nav does, so onboarding content need not target CRM fields.
  const { hasAccess: groupClassesEnabled, isLoading: isGroupClassesLoading } = useOrgGroupAccess({
    configPath: FeatureAccessPath.LIVE_SESSIONS_GROUP_CLASSES_ENABLED,
    restrictedRoles: [ClerkUserRole.LEARNER, ClerkUserRole.COACH],
  })
  const { isEnabled: assessmentsEnabled, isLoading: isAssessmentsAccessLoading } = useAssessmentsAccess({
    context: 'learner',
  })
  // The assessments check includes an Unleash flag, so it also waits on flags.
  const accessReady = !isGroupClassesLoading && !isAssessmentsAccessLoading && flagsReady

  // /users/me does not revalidate on focus, and the coaching traits go stale while a tab sits open.
  const clerkUserId = user?.id
  useEffect(() => {
    if (!clerkUserId) return
    let lastRefreshAt = Date.now()
    const onFocus = () => {
      if (document.visibilityState === 'hidden') return
      const now = Date.now()
      if (now - lastRefreshAt < FOCUS_REFRESH_INTERVAL_MS) return
      lastRefreshAt = now
      void refetchCurrentUser()
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [clerkUserId, refetchCurrentUser])

  useEffect(() => {
    // Wait for group name if user has a group_id
    const groupLoading = userDetails?.group_id && groupName === undefined

    if (user && !groupLoading) {
      const isImpersonation = !!clerk.session?.actor
      const authMethod = userDetails?.last_sign_in_method ?? null
      const trial = userDetails?.trial_summary

      Userpilot.identify(user.id, {
        name: user.fullName,
        email: user.primaryEmailAddress?.emailAddress,
        created_at: user.createdAt?.toISOString(),
        // The English name, not a locale: live survey segments target it by name.
        language: userDetails?.learner_info?.native_language,
        // Userpilot's localization (ISO 639-1); browser_language is the device's, the admin's when impersonating.
        locale_code: localeCode,
        proficiency_level: userDetails?.learner_info?.level,
        role: userRole,
        role_name: formatRoleName(userRole),
        group_id: userDetails?.group_id,
        group_name: groupName,
        // Cleared, not left out, when there is no trial summary: a deleted trial or a role change would leave the old values.
        ...(userDetails && {
          ac_status: trial?.ac_status ?? CLEARED,
          // 'expired' once the trial ended with eligibility never resolved, as the eligibility badge shows.
          eligibility_status: trial
            ? isEligibilityExpired(trial.eligibility_status, trial.trial_state)
              ? 'expired'
              : trial.eligibility_status
            : CLEARED,
          trial_state: trial?.trial_state ?? CLEARED,
          // Converting overwrites the enrollment end date with the program end, so read the trial window.
          trial_end_date:
            (trial &&
              trialWindowEndDate({
                trial_state: trial.trial_state,
                end_date: trial.trial_end_date,
                converted_at: trial.converted_at,
              })) ??
            CLEARED,
          trial_days_remaining: (trial && trialWindowDaysRemaining(trial)) ?? CLEARED,
          ac_lessons_to_go: trial?.ac_lessons_to_go ?? CLEARED,
          // The raw instant, unlike the Eastern Time dates above.
          trial_converted_at: trial?.converted_at ?? CLEARED,
          license_end_date: userDetails.license_end_date ?? CLEARED,
          enrollment_start_date: userDetails.current_enrollment_start_date ?? CLEARED,
        }),
        last_authentication_method: authMethod,
        is_impersonation: isImpersonation,
        // Only once flags load, or treatment users read 'control' while Unleash loads.
        ...(flagsReady && {
          featured_lessons_experiment: featuredLessonsEnabled ? 'treatment' : 'control',
        }),
        // Only once both lookups resolve, so a loading render cannot release a tour over a pending modal.
        ...(onboardingReady && {
          onboarding_stage: onboardingStage,
          assessment_due_at: assessmentDueAtIso ?? CLEARED,
        }),
        ...(accessReady && {
          group_classes_enabled: groupClassesEnabled,
          assessments_enabled: assessmentsEnabled,
        }),
        ...(userDetails && {
          has_coach: userDetails.has_coach === true,
          coach_assigned_at: userDetails.coach_assigned_at ?? CLEARED,
          next_coaching_meeting_at: userDetails.next_coaching_meeting_at ?? CLEARED,
          last_coaching_meeting_at: userDetails.last_coaching_meeting_at ?? CLEARED,
          // Learners only: a staff user gets no count.
          coaching_meetings_attended: userDetails.coaching_meetings_attended ?? undefined,
          is_coaching_manager: userDetails.can_manage_coaching === true,
        }),
        ...(userDetails &&
          coachingFlagReady && {
            coaching_enabled: learnerCoachingFlag && userDetails.coaching_enabled === true,
          }),
        company: {
          id: user.organizationMemberships?.[0]?.organization.id,
          name: user.organizationMemberships?.[0]?.organization.name,
          slug: user.organizationMemberships?.[0]?.organization.slug,
          created_at: user.organizationMemberships?.[0]?.organization.createdAt?.toISOString(),
        },
      })

      const sessionId = clerk.session?.id
      if (sessionId && userDetails && !hasTrackedSignIn(sessionId)) {
        Userpilot.track('user_signed_in', {
          authentication_method: authMethod,
          is_impersonation: isImpersonation,
        })
        markSignInTracked(sessionId)
      }
    }
  }, [
    user,
    userRole,
    userDetails,
    groupName,
    clerk,
    featuredLessonsEnabled,
    flagsReady,
    localeCode,
    onboardingReady,
    onboardingStage,
    assessmentDueAtIso,
    accessReady,
    groupClassesEnabled,
    assessmentsEnabled,
    learnerCoachingFlag,
    coachingFlagReady,
  ])
}
